import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { ThemeMode } from '../theme';

const STORAGE_KEY = 'barahpay.admin.theme';

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeState | null>(null);

/** The stored choice, else the operating system's preference — the Barah Plus default. */
function initialMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch {
    // Storage can be blocked; fall through to the system preference.
  }

  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Paints the document for a mode. Both the `.dark` class (the Barah Plus convention, which brand.css keys
 * the dark palette on) and `data-bp-theme` are set, so plain CSS and the antd theme agree.
 */
function applyMode(mode: ThemeMode): void {
  const root = document.documentElement;
  root.classList.toggle('dark', mode === 'dark');
  root.dataset.bpTheme = mode;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const initial = initialMode();
    // Applied during the first render, not in an effect, so the page never flashes the wrong theme.
    applyMode(initial);
    return initial;
  });

  const setMode = useCallback((next: ThemeMode) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // A blocked store only costs the choice surviving a reload.
    }

    applyMode(next);
    setModeState(next);
  }, []);

  const value = useMemo<ThemeState>(
    () => ({ mode, setMode, toggleMode: () => setMode(mode === 'dark' ? 'light' : 'dark') }),
    [mode, setMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeMode(): ThemeState {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useThemeMode must be used inside a ThemeProvider.');
  }

  return context;
}
